import { Component, OnDestroy, computed, effect, inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, Observable, Subject, Subscription, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { AppleDeviceProfileResolverActions } from '@era/stores';
import { Store } from '@ngrx/store';
import { isOsVersionAtLeast } from '@rocksoft/shared';
import { ToastService } from '@rocksoft/shared/toast';
import { AppleCommandProgressService } from '../../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../../../utilities/command-spinner/command-spinner.component';
import { AppleDevice } from '../../../../viewmodels/apple/apple-device.model';
import { QueryList } from '../../../../viewmodels/apple/commands/device-information.command';
import { CommandProgress } from '../../../../viewmodels/apple/signalr/command-context.model';

@Component({
  selector: 'ml-mdm-enable-lost-mode',
  templateUrl: './enable-lost-mode.component.html',
  imports: [CommandSpinnerComponent, ReactiveFormsModule],
})
export class EnableLostModeComponent implements OnDestroy {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly appleMdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly store = inject(Store);
  private readonly toastService = inject(ToastService);

  readonly apple = input.required<AppleDevice>();
  enableLostModeForm: UntypedFormGroup;

  private _commandProgress: Subject<CommandProgress> = new Subject<CommandProgress>();
  private _commandProgressInformation: Subject<CommandProgress> = new Subject<CommandProgress>();

  private readonly _udidSignal = computed(() => this.apple().udid);

  readonly commandProgress$: Observable<CommandProgress> = toObservable(this._udidSignal).pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'EnableLostMode'))
    )
  );

  readonly commandProgressInformation$: Observable<CommandProgress> = toObservable(this._udidSignal).pipe(
    switchMap((_udid) =>
      merge(
        this._commandProgressInformation,
        this.commandProgressService.commandsOfRequestType(_udid, 'DeviceInformation')
      )
    )
  );

  private _isProcessing = new BehaviorSubject(false);
  isEnabled: Observable<boolean | null>;

  queryList: QueryList = new QueryList();

  success = false;

  readonly isLostModeEnabled = computed(() => this.apple().isMDMLostModeEnabled ?? false);
  readonly isSupervised = computed(() => this.apple()?.isSupervised ?? false);
  readonly hasSupportedOS = computed(() => isOsVersionAtLeast(this.apple().osVersion, '9.3.0'));

  readonly udid = computed(() => this.apple().udid);

  private readonly subscriptions: Subscription[] = [];

  constructor() {
    this.createForm();
    this.isEnabled = this.isProcessing.pipe(
      map((isProcessing) => {
        return (
          this.isSupervised() &&
          !this.isLostModeEnabled() &&
          !isProcessing &&
          (this.hasSupportedOS() == null || this.hasSupportedOS())
        );
      })
    );
    effect(() => {
      void this.apple();
      this.enableLostModeForm.reset({
        lostModeMessage: '',
        lostModePhoneNumber: '',
        lostModeFootnote: '',
      });

      if (this.isEnabled) {
        this.enableLostModeForm.enable();
      } else {
        this.enableLostModeForm.disable();
      }
    });

    this.subscriptions.push(
      this.commandProgress$
        .pipe(
          filter((progress) => progress.status === 'Acknowledged'),
          distinctUntilChanged()
        )
        .subscribe(() => {
          this.queryDeviceInformation();
        })
    );

    // TODO: Move to central service
    this.subscriptions.push(
      this.commandProgressInformation$
        .pipe(
          filter((progress) => progress.status === 'Acknowledged'),
          distinctUntilChanged()
        )
        .subscribe((progress) => {
          this.store.dispatch(
            AppleDeviceProfileResolverActions.profileRequested({
              udid: progress.udid,
              profileType: 'device-information',
            })
          );
        })
    );
  }

  get isProcessing(): Observable<boolean> {
    return this._isProcessing;
  }

  get lostModeMessage(): AbstractControl | null {
    return this.enableLostModeForm.get('lostModeMessage');
  }

  get lostModePhoneNumber(): AbstractControl | null {
    return this.enableLostModeForm.get('lostModePhoneNumber');
  }

  get lostModeFootnote(): AbstractControl | null {
    return this.enableLostModeForm.get('lostModeFootnote');
  }

  onSubmit() {
    const udid = this.udid();

    if (this.enableLostModeForm.status !== 'VALID') {
      return;
    }

    this._isProcessing.next(true);
    this.success = false;

    const command: CommandProgress = {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'EnableLostMode',
    };
    this._commandProgress.next(command);

    this.subscriptions.push(
      this.appleMdmService
        .enableLostMode(
          udid,
          this.lostModeMessage?.value,
          this.lostModePhoneNumber?.value,
          this.lostModeFootnote?.value
        )
        .subscribe({
          next: () => {
            this.success = true;
            this._isProcessing.next(false);
            this.enableLostModeForm.markAsPristine();
          },
          error: () => {
            this._isProcessing.next(false);
            this.enableLostModeForm.markAsPristine();
          },
        })
    );
  }

  private queryDeviceInformation() {
    const udid = this.udid();
    const command: CommandProgress = {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'DeviceInformation',
    };
    this._commandProgressInformation.next(command);

    this.subscriptions.push(this.appleMdmService.getDeviceInformation(udid).subscribe());
  }

  private createForm() {
    this.enableLostModeForm = this.fb.group({
      lostModeMessage: [''],
      lostModePhoneNumber: [''],
      lostModeFootnote: [''],
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
